// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Icon, Space, Styled} from '@supermove/components';
import {gql, useQuery} from '@supermove/graphql';
import {
  ResponsiveType,
  useModal,
  useEffect,
  useNavigationDOM,
  useResponsive,
  useState,
  useTrackEvent,
} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';
import {colors, Typography} from '@supermove/styles';

// App
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import ErrorState from '@shared/design/components/EmptyState/ErrorState';
import TreeNavigation from '@shared/design/components/TreeNavigation';
import ErrorCatcher from '@shared/modules/Error/components/ErrorCatcher';
import ProjectBlockKind from '@shared/modules/Project/enums/ProjectBlockKind';
import ProjectWidgetKind from '@shared/modules/Project/enums/ProjectWidgetKind';
import Line from 'modules/App/components/Line';
import {useCommunicationInboxProjectWidgetUrlFilters} from 'modules/Communication/CommunicationInbox/Project/useCommunicationInboxProjectWidgetUrlFilters';
import {CommunicationInboxConversationUrlFilterType} from 'modules/Communication/CommunicationInbox/types/CommunicationInboxUrlFilterType';
import ProjectWidgetsToolbar from 'modules/Project/V2/Show/components/ProjectWidgetsToolbar';
import JobsWidget from 'modules/Project/V2/Show/components/widgets/JobsWidget';
import ProjectAttachmentsWidget from 'modules/Project/V2/Show/components/widgets/ProjectAttachmentsWidget';
import ProjectCommentsWidget from 'modules/Project/V2/Show/components/widgets/ProjectCommentsWidget';
import ProjectDetailsWidget from 'modules/Project/V2/Show/components/widgets/ProjectDetailsWidget';
import ProjectDocumentsWidget from 'modules/Project/V2/Show/components/widgets/ProjectDocumentsWidget';
import ProjectEventLogWidget from 'modules/Project/V2/Show/components/widgets/ProjectEventLogWidget';
import ProjectTasksWidget from 'modules/Project/V2/Show/components/widgets/ProjectTasksWidget';
import ProjectWorkflowsWidget from 'modules/Project/V2/Show/components/widgets/ProjectWorkflowsWidget';
import QuitEditingWidgetModal from 'modules/Project/V2/Show/components/widgets/QuitEditingWidgetModal';
import {ProjectPageContextProvider} from 'modules/Project/V2/Show/hooks/useProjectPageContext';

const VerticalDivider = Styled.View`
  height: 100%;
  width: 1px;
  background-color: ${colors.gray.border}
`;

const ProjectWidgetsContainer = Styled.View<{responsive: ResponsiveType}>`
  flex-direction: row;
  background-color: ${colors.white};
  ${({responsive}: {responsive: ResponsiveType}) => !responsive.desktop && 'width: 100%;'}
`;

const NoProjectContainer = Styled.View<{responsive: ResponsiveType}>`
  align-items: center;
  justify-content: center;
  padding-top: 100px;
  ${({responsive}: {responsive: ResponsiveType}) => (responsive.desktop ? 'width: 400px;' : 'flex: 1')}
`;

const ProjectPlaceholderTitle = Styled.Text`
  ${Typography.Responsive.Heading2}
  color: ${colors.gray.secondary};
`;

const ProjectPlaceholderDescription = Styled.Text`
  ${Typography.Responsive.Body}
  color: ${colors.gray.tertiary};
`;

const WidgetButtonsContainer = Styled.View`
`;

const WidgetContentContainer = Styled.View<{responsive: ResponsiveType}>`
  width: ${({responsive}) => (responsive.desktop ? '344px' : undefined)};
  ${({responsive}: {responsive: ResponsiveType}) => !responsive.desktop && 'flex: 1;'}
`;

const ContentHeaderContainer = Styled.View`
  flex-direction: row;
  padding-vertical: 16px;
  align-items: center;
`;

const ContentHeaderText = Styled.Text`
  ${Typography.Heading2}
`;

const LoadingComponent = ({responsive}: {responsive: ResponsiveType}) => {
  return (
    <NoProjectContainer responsive={responsive}>
      <WidgetButtons isDisabled />
    </NoProjectContainer>
  );
};

const ProjectWidgetContent = ({
  project,
  setIsClosable,
  urlFilters,
}: {
  project: ProjectModel;
  setIsClosable: (isClosable: boolean) => void;
  urlFilters: CommunicationInboxConversationUrlFilterType;
}) => {
  const {params} = useNavigationDOM();
  const responsive = useResponsive();

  if (params.widget === 'MENU' && responsive.desktop) {
    urlFilters.handleUpdate({widget: undefined});
  }

  switch (params.widget) {
    case ProjectWidgetKind.ATTACHMENTS:
      return <ProjectAttachmentsWidget project={project} />;
    case ProjectWidgetKind.COMMENTS:
      return <ProjectCommentsWidget projectUuid={project.uuid} setIsClosable={setIsClosable} />;
    case ProjectWidgetKind.DOCUMENTS:
      return <ProjectDocumentsWidget projectUuid={project.uuid} urlFilters={urlFilters} />;
    case ProjectWidgetKind.TASKS:
      return <ProjectTasksWidget projectUuid={project.uuid} urlFilters={urlFilters} />;
    case ProjectWidgetKind.EVENT_LOG:
      return <ProjectEventLogWidget projectUuid={project.uuid} urlFilters={urlFilters} />;
    case ProjectWidgetKind.WORKFLOWS:
      return <ProjectWorkflowsWidget projectUuid={project.uuid} urlFilters={urlFilters} />;
    case ProjectWidgetKind.PROJECT_DETAILS:
      return <ProjectDetailsWidget projectUuid={project.uuid} urlFilters={urlFilters} />;
    case ProjectWidgetKind.JOB_DETAILS:
      return <JobsWidget projectUuid={project.uuid} urlFilters={urlFilters} />;
    default:
      return null;
  }
};

const WidgetContentHeader = ({handleClose}: {handleClose: () => void}) => {
  const {params} = useNavigationDOM();
  return (
    <ContentHeaderContainer>
      <Space width={16} />
      <ContentHeaderText>{ProjectWidgetKind.getDisplay(params.widget)}</ContentHeaderText>
      <Space style={{flex: 1}} />
      <TertiaryButton onPress={handleClose}>
        <Icon source={Icon.XmarkLarge} size={12} color={colors.gray.primary} />
      </TertiaryButton>
      <Space width={18} />
    </ContentHeaderContainer>
  );
};

const WidgetButtons = ({
  urlFilters,
  isDisabled,
  project,
}: {
  urlFilters?: CommunicationInboxConversationUrlFilterType;
  isDisabled?: boolean;
  project?: ProjectModel;
}) => {
  const {params} = useNavigationDOM();
  return (
    <WidgetButtonsContainer>
      <Space height={24} />
      <TreeNavigation
        navigationItems={
          isDisabled || !project
            ? []
            : ProjectWidgetKind.getCommunicationInboxWidgets(project).map((kind) => {
                return {
                  icon: ProjectWidgetKind.getIcon(kind),
                  tooltip: ProjectWidgetKind.getDisplay(kind),
                  value: kind,
                  valueKey: 'widget',
                };
              })
        }
        handleSetValues={(values) => urlFilters?.handleUpdate(values)}
        values={params}
        width={56}
        spacing={24}
        isCentered
        isDisabled={isDisabled}
        tooltipPlacement={'left'}
      />
    </WidgetButtonsContainer>
  );
};

const handleCloseWidget = ({
  urlFilters,
}: {
  urlFilters: CommunicationInboxConversationUrlFilterType;
}) => {
  urlFilters.handleUpdate({widget: undefined, section: undefined, block: undefined});
};

const CommunicationInboxProjectWidgets = ({
  urlFilters,
}: {
  urlFilters: CommunicationInboxConversationUrlFilterType;
}) => {
  const responsive = useResponsive();
  const projectUrlFilters = useCommunicationInboxProjectWidgetUrlFilters(urlFilters);
  const projectUuid = projectUrlFilters.get('projectUuid');

  const {data, loading} = useQuery<{project: ProjectModel}>(
    CommunicationInboxProjectWidgets.query,
    {
      fetchPolicy: 'cache-and-network',
      variables: {projectUuid},
      skip: !projectUuid,
    },
  );

  const {params} = useNavigationDOM();
  const [isClosable, setIsClosable] = useState(true);
  const quitEditingModal = useModal({name: 'Quit Editing Modal'});

  const [prevWidget, setPrevWidget] = useState<string>();
  const {trackEvent: trackWidgetEvent} = useTrackEvent({
    // Event name should not be changed. It should be derived from the enum, made human readable.
    eventName: `Viewed ${_.startCase(_.toLower(urlFilters.get('widget')))} Widget`,
    eventType: `Communication Inbox Widget View`,
  });

  useEffect(() => {
    // Project details tracks its own analytics
    if (
      urlFilters.get('widget') !== prevWidget &&
      urlFilters.get('widget') !== ProjectWidgetKind.PROJECT_DETAILS
    ) {
      trackWidgetEvent();
      setPrevWidget(urlFilters.get('widget'));
    }
  }, [urlFilters.get('widget')]);

  // The job block doesn't exist here, redirect to job widget
  useEffect(() => {
    if (urlFilters.get('block') === ProjectBlockKind.JOBS) {
      urlFilters.handleUpdate({widget: ProjectWidgetKind.JOB_DETAILS, block: undefined});
    }
  }, [urlFilters.get('block')]);

  if (!projectUuid) {
    return (
      <NoProjectContainer responsive={responsive}>
        <ProjectPlaceholderTitle responsive={responsive}>
          No conversation selected
        </ProjectPlaceholderTitle>
        <Space height={16} />
        <ProjectPlaceholderDescription responsive={responsive}>
          Select a conversation to view details
        </ProjectPlaceholderDescription>
      </NoProjectContainer>
    );
  }

  if (loading || !data?.project) {
    return <LoadingComponent responsive={responsive} />;
  }

  return (
    <ProjectWidgetsContainer responsive={responsive}>
      <ProjectPageContextProvider isWidget>
        {responsive.desktop ? (
          <WidgetButtons urlFilters={projectUrlFilters} project={data.project} />
        ) : null}
        {params.widget && (
          <React.Fragment>
            <VerticalDivider />
            <WidgetContentContainer responsive={responsive}>
              <WidgetContentHeader
                handleClose={() =>
                  isClosable ? handleCloseWidget({urlFilters}) : quitEditingModal.handleOpen()
                }
              />
              <Line />
              <ErrorCatcher
                ErrorComponent={() => (
                  <ErrorState.Centered title={ProjectWidgetKind.getErrorTitle(params.widget)} />
                )}
              >
                <ProjectWidgetContent
                  project={data.project}
                  setIsClosable={setIsClosable}
                  urlFilters={projectUrlFilters}
                />
                {responsive.desktop ? null : (
                  <ProjectWidgetsToolbar
                    urlFilters={urlFilters}
                    project={data.project}
                    widgets={ProjectWidgetKind.getCommunicationInboxWidgets(data.project)}
                  />
                )}
              </ErrorCatcher>
            </WidgetContentContainer>
          </React.Fragment>
        )}
        <QuitEditingWidgetModal
          quitEditingModal={quitEditingModal}
          handleCloseParent={() => handleCloseWidget({urlFilters})}
        />
      </ProjectPageContextProvider>
    </ProjectWidgetsContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
CommunicationInboxProjectWidgets.query = gql`
  ${ProjectAttachmentsWidget.fragment}
  ${ProjectWidgetsToolbar.fragment}
  ${ProjectWidgetKind.getCommunicationInboxWidgets.fragment}

  query CommunicationInboxProjectWidgets($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      uuid
      ...ProjectAttachmentsWidget
      ...ProjectWidgetsToolbar
      ...ProjectWidgetKind_getCommunicationInboxWidgets
    }
  }
`;

export default CommunicationInboxProjectWidgets;
